import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "urql";
import { AvoRedApp } from "../components/Layout/AvoRedApp";
import { CategoryProducts } from "../components/CategoryProducts";
import { CategoryCard } from "../components/CategoryCard";
import { CategoryBanner } from "../components/CategoryBanner";
import { TrustedBy } from "../components/TrustedBy";
import { Category } from "../types/ProductType";
import { 
  ROOT_CATEGORIES_QUERY, 
  CUSTOMER_QUERY,
} from "../graphql/queries/categoryQuery";
import { useSelector } from "react-redux";
import { RootState } from "../app/store";
import { Toast } from "../components/Toast";

export const Index = () => {
  const isAuth = useSelector((state: RootState) => state.userLogin.isAuth);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [toast, setToast] = useState<{
    isVisible: boolean;
    message: string;
    type: "success" | "error";
  }>({
    isVisible: false,
    message: "",
    type: "success",
  });

  // Query để lấy customer info (nếu đã login)
  const [{ data: customerData }] = useQuery({
    query: CUSTOMER_QUERY,
    pause: !isAuth,
  });

  // Query để lấy root categories
  const [{ data: categoriesData, fetching: categoriesFetching }] = useQuery({
    query: ROOT_CATEGORIES_QUERY,
    requestPolicy: "network-only"
  });

  const customerGroup = customerData?.customerQuery?.group;
  const categories = categoriesData?.allCategory || [];

  // Filter categories theo customer group nếu có, hoặc lấy tất cả nếu không có
  const filteredCategories = React.useMemo(() => {
    if (!categories || categories.length === 0) return [];
    
    // Nếu có customer group, có thể filter categories theo customer group
    // Hiện tại lấy tất cả categories, có thể extend sau
    return categories;
  }, [categories]);

  // Auto slide carousel
  React.useEffect(() => {
    if (filteredCategories.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % Math.min(filteredCategories.length, 5));
    }, 5000);
    return () => clearInterval(interval);
  }, [filteredCategories.length]);

  const handleAddToCart = (success: boolean, message: string) => {
    setToast({
      isVisible: true,
      message,
      type: success ? "success" : "error",
    });
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  return (
    <AvoRedApp>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Slider */}
        {filteredCategories.length > 0 && (
          <section className="relative bg-white">
            <div className="relative h-96 overflow-hidden">
              {filteredCategories.slice(0, 5).map((category: Category, index: number) => (
                <div
                  key={category.id}
                  className={`absolute inset-0 transition-opacity duration-500 ${
                    index === currentSlide ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <Link to={`/category/${category.slug}`}>
                    <CategoryBanner category={category} height="h-96" />
                  </Link>
                </div>
              ))}
              
              {/* Slide indicators */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                {filteredCategories.slice(0, 5).map((_: Category, index: number) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      index === currentSlide ? "bg-white" : "bg-white/50"
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>

              {/* Navigation arrows */}
              <button
                onClick={() => setCurrentSlide((prev) => (prev - 1 + Math.min(filteredCategories.length, 5)) % Math.min(filteredCategories.length, 5))}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full transition-colors z-10"
                aria-label="Previous slide"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => setCurrentSlide((prev) => (prev + 1) % Math.min(filteredCategories.length, 5))}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full transition-colors z-10"
                aria-label="Next slide"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </section>
        )}

        {/* Categories Grid */}
        <section className="py-12 bg-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Danh mục sản phẩm
            </h2>
            
            {categoriesFetching ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-32 bg-gray-200 rounded-lg"></div>
                    <div className="h-4 bg-gray-200 rounded mt-2 w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : filteredCategories.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {filteredCategories.map((category: Category) => (
                  <CategoryCard key={category.id} category={category} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">Không có danh mục nào</p>
              </div>
            )}
          </div>
        </section>

        {/* Products by Category - For logged in users with customer group */}
        {isAuth && customerGroup && filteredCategories.length > 0 && (
          <section className="py-12 bg-gray-50">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="mb-8 text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Sản phẩm dành cho nhóm {customerGroup.name}
                </h2>
              </div>
              {filteredCategories.slice(0, 3).map((category: Category) => (
                <CategoryProducts
                  key={category.id}
                  categoryId={category.id}
                  categoryName={category.name}
                  categorySlug={category.slug}
                  perPage={4}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          </section>
        )}

        {/* Products by Category - For public users (no customer group) */}
        {(!isAuth || !customerGroup) && filteredCategories.length > 0 && (
          <section className="py-12 bg-gray-50">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="mb-8 text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Sản phẩm nổi bật
                </h2>
                <p className="text-gray-600">
                  Khám phá các sản phẩm mới nhất và phổ biến nhất
                </p>
              </div>
              {filteredCategories.slice(0, 3).map((category: Category) => (
                <CategoryProducts
                  key={category.id}
                  categoryId={category.id}
                  categoryName={category.name}
                  categorySlug={category.slug}
                  perPage={4}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          </section>
        )}

        {/* Trusted By Section */}
        <TrustedBy contractorCount={15320} />

        {/* Empty state */}
        {!categoriesFetching && filteredCategories.length === 0 && (
          <section className="py-12">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Chào mừng đến với cửa hàng của Menas
              </h2>
              <p className="text-gray-600">
                Hiện tại chưa có danh mục sản phẩm nào.
              </p>
            </div>
          </section>
        )}
      </div>

      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={handleCloseToast}
      />
    </AvoRedApp>
  );
};
